import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import Card from "@/components/common/Card";
import IconPicker from "@/components/common/IconPicker";

const AI_STATUS_STEPS = [
  "Understanding your description",
  "Identifying key personas",
  "Finding relevant behaviors",
  "Building look-alike models",
];

export default function Step1Define({
  form,
  updateField,
  onGenerate,
  isGenerating,
  aiError,
  aiSummary,
}) {
  const [revealedSteps, setRevealedSteps] = useState(0);

  useEffect(() => {
    if (!isGenerating) return undefined;
    setRevealedSteps(0);
    let i = 0;
    const interval = setInterval(() => {
      i += 1;
      setRevealedSteps(i);
      if (i >= AI_STATUS_STEPS.length) clearInterval(interval);
    }, 650);
    return () => clearInterval(interval);
  }, [isGenerating]);

  return (
    <Card className="flex flex-col gap-5">
      <div className="form-group-modal">
        <label htmlFor="segment-name">Audience name</label>
        <input
          id="segment-name"
          value={form.name}
          onChange={(e) => updateField("name", e.target.value)}
          placeholder="e.g. Wellness Enthusiasts"
          required
        />
      </div>
      <div className="form-group-modal">
        <IconPicker
          label="Icon"
          value={form.icon ?? ""}
          onChange={(next) => updateField("icon", next || null)}
        />
      </div>
      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-(--text-primary)">
              Describe your ideal audience
            </h3>
            <span className="inline-flex items-center gap-1 rounded-(--radius-pill) bg-(--pastel-violet) px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-(--brand-primary)">
              AI
            </span>
          </div>
          <p className="mt-1 text-sm text-(--text-secondary)">
            Tell our AI who you want to reach and what matters to your campaign.
          </p>
          <div className="relative mt-3">
            <textarea
              value={form.description ?? ""}
              onChange={(e) =>
                updateField("description", e.target.value.slice(0, 500))
              }
              rows={5}
              maxLength={500}
              placeholder="Describe your target audience…"
              className="block w-full resize-y rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1) p-3 pr-16 text-sm text-(--text-primary) placeholder:text-(--text-muted) focus:border-(--brand-primary) focus:outline-none focus:ring-2 focus:ring-(--brand-primary)/20"
            />
            <span className="pointer-events-none absolute bottom-2 right-3 text-xs text-(--text-muted)">
              {(form.description ?? "").length} / 500
            </span>
          </div>
          {(form.description ?? "").length > 0 && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => updateField("description", "")}
                className="text-sm font-medium text-(--brand-primary) hover:underline"
              >
                Clear prompt
              </button>
            </div>
          )}
          {aiError && (
            <p className="mt-3 text-sm text-(--status-danger)">{aiError}</p>
          )}
          {aiSummary && !aiError && (
            <p className="mt-3 text-sm text-(--status-success)">{aiSummary}</p>
          )}
        </div>
        <div className="rounded-(--radius-lg) bg-(--surface-2) p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-(--radius-pill) border-2 ${
                  isGenerating
                    ? "animate-spin border-(--brand-primary) border-t-transparent"
                    : "border-(--border-subtle)"
                }`}
                aria-hidden="true"
              />
              <h4 className="text-sm font-semibold text-(--text-primary)">
                {isGenerating
                  ? "AI is analyzing…"
                  : revealedSteps === AI_STATUS_STEPS.length
                    ? "AI is done"
                    : "AI is ready"}
              </h4>
            </div>
            <button
              type="button"
              onClick={onGenerate}
              disabled={isGenerating || !(form.description ?? "").trim()}
              className="inline-flex h-9 items-center gap-2 rounded-(--radius-md) bg-(--brand-primary) px-3 text-sm font-semibold text-(--brand-primary-contrast) hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Sparkles size={14} strokeWidth={2} />
              {isGenerating ? "Generating…" : "Generate"}
            </button>
          </div>
          {revealedSteps > 0 && (
            <ul className="mt-4 flex flex-col gap-3 text-sm">
              {AI_STATUS_STEPS.slice(0, revealedSteps).map((step) => (
                <li
                  key={step}
                  className="flex items-center gap-3 transition-opacity"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-(--radius-pill) bg-(--brand-primary) text-xs font-bold text-(--brand-primary-contrast)">
                    ✓
                  </span>
                  <span className="text-(--text-primary)">{step}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}
